// Nine Lives '84 - tune notation.
//
// A tune is a string of tokens like `E5:2` (note name, octave, length in
// eighths) or `R:1` (a rest). Sharps are `#`, flats `b`. `parseTune` turns
// it into frequencies and seconds at a tempo, ready for the square-wave
// player. The theme below is original.

import type { TuneNote } from './audio';

/** A tune: the melody, and an optional bass line of the same length. */
export interface Tune {
  melody: TuneNote[];
  bass?: TuneNote[];
}

export function tuneSeconds(notes: TuneNote[]): number {
  return notes.reduce((s, n) => s + n.secs, 0);
}

const SEMITONES: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** Frequency of a note name like `A4` or `F#5`. */
export function noteFrequency(name: string): number {
  const m = /^([A-G])([#b]?)(\d)$/.exec(name);
  if (!m) throw new Error(`bad note: ${name}`);
  let semis = SEMITONES[m[1]] + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0);
  semis += (Number(m[3]) - 4) * 12;
  // Relative to A4 = 440 Hz, which is 9 semitones above C4.
  return 440 * Math.pow(2, (semis - 9) / 12);
}

export function parseTune(text: string, bpm: number): TuneNote[] {
  const eighth = 60 / bpm / 2;
  const notes: TuneNote[] = [];
  for (const raw of text.split(/\s+/)) {
    const token = raw.trim();
    if (!token || token === '|') continue;
    const [name, len] = token.split(':');
    const secs = (Number(len) || 1) * eighth;
    notes.push({ freq: name === 'R' ? 0 : noteFrequency(name), secs });
  }
  return notes;
}

/** "Rooftop Prowl": a low, sparse walking loop for the alley and the rooms. */
export const PROWL: Tune = {
  melody: parseTune(
  `
  A3:2 R:1 C4:1 E4:2 R:2 | G4:1 E4:1 D4:2 C4:2 A3:2 | A3:2 R:1 C4:1 E4:2 G4:2 | A4:3 G4:1 E4:2 D4:2 |
  C4:2 R:1 E4:1 G4:2 R:2 | A4:1 G4:1 E4:2 D4:2 C4:2 | D4:2 R:1 F4:1 A4:2 G4:2 | E4:4 R:4 |
  A3:2 R:1 C4:1 E4:2 R:2 | G4:1 E4:1 D4:2 C4:2 A3:2 | E4:2 R:1 G4:1 A4:2 C5:2 | B4:3 A4:1 G4:2 E4:2 |
  D4:2 R:1 E4:1 F4:2 R:2 | G4:1 F4:1 E4:2 D4:2 C4:2 | B3:2 D4:2 E4:2 G4:2 | A3:6 R:2
  `,
    140,
  ),
};

/** "Valentine Waltz": sixteen bars in three for Clementine's room. */
export const WALTZ: Tune = {
  melody: parseTune(
  `
  E5:2 G5:2 B5:2 | A5:4 G5:2 | F5:2 A5:2 C6:2 | B5:4 A5:2 | G5:2 B5:2 D6:2 | C6:4 B5:2 | A5:2 G5:2 F5:2 | E5:6 |
  E5:2 G5:2 B5:2 | A5:4 G5:2 | F5:2 A5:2 C6:2 | B5:4 A5:2 | C6:2 B5:2 A5:2 | G5:4 F5:2 | E5:2 D5:2 F5:2 | E5:6
  `,
    150,
  ),
  // A waltz bass: root, then two chord taps.
  bass: parseTune(
    `
  C3:2 E3:2 G3:2 | F3:2 A3:2 C4:2 | F3:2 A3:2 C4:2 | G3:2 B3:2 D4:2 | G3:2 B3:2 D4:2 | C3:2 E3:2 G3:2 | F3:2 A3:2 C4:2 | C3:2 E3:2 G3:2 |
  C3:2 E3:2 G3:2 | F3:2 A3:2 C4:2 | F3:2 A3:2 C4:2 | G3:2 B3:2 D4:2 | A3:2 C4:2 E4:2 | G3:2 B3:2 D4:2 | G3:2 B3:2 D4:2 | C3:2 E3:2 G3:2
  `,
    150,
  ),
};

/**
 * "Back Fence Rag": the title tune. An original honky-tonk rag in C, in
 * the spirit of the jaunty piano piece the 1984 game borrowed (which is
 * copyrighted, so nothing of it is quoted): a chirpy syncopated melody
 * over a two-beat oom-pah bass.
 */
export const THEME: Tune = {
  melody: parseTune(
    `
  E5:1 G5:1 C6:2 G5:1 E5:1 G5:2 | A5:1 G5:1 E5:2 C5:1 D5:1 E5:2 |
  F5:1 A5:1 C6:2 A5:1 F5:1 A5:2 | G5:1 E5:1 C5:2 R:1 E5:1 G5:2 |
  E5:1 G5:1 C6:2 G5:1 E5:1 G5:2 | A5:1 G5:1 E5:2 C5:1 D5:1 E5:2 |
  D5:1 F5:1 A5:2 F5:1 D5:1 B4:2 | C5:4 R:2 G4:1 B4:1 |
  C5:1 E5:1 G5:2 E5:1 C5:1 E5:2 | D5:1 F5:1 A5:2 F5:1 D5:1 F5:2 |
  E5:1 G5:1 C6:2 A5:1 G5:1 E5:2 | D5:2 G5:2 B4:2 D5:2 |
  E5:1 G5:1 C6:2 G5:1 E5:1 G5:2 | A5:1 C6:1 A5:2 G5:1 E5:1 D5:2 |
  C5:1 E5:1 G5:1 E5:1 D5:1 B4:1 D5:1 F5:1 | E5:2 C5:2 C5:2 R:2
  `,
    184,
  ),
  // Oom-pah: root on the beat, the fifth off it.
  bass: parseTune(
    `
  C3:2 G3:2 C3:2 G3:2 | C3:2 G3:2 C3:2 G3:2 | F3:2 C4:2 F3:2 C4:2 | C3:2 G3:2 G3:2 D4:2 |
  C3:2 G3:2 C3:2 G3:2 | C3:2 G3:2 C3:2 G3:2 | G3:2 D4:2 G3:2 D4:2 | C3:2 G3:2 C3:2 G3:2 |
  C3:2 G3:2 C3:2 G3:2 | F3:2 C4:2 F3:2 C4:2 | C3:2 G3:2 C3:2 G3:2 | G3:2 D4:2 G3:2 D4:2 |
  C3:2 G3:2 C3:2 G3:2 | F3:2 C4:2 F3:2 C4:2 | G3:2 D4:2 G3:2 D4:2 | C3:2 G3:2 C3:2 R:2
  `,
    184,
  ),
};
